import React from 'react';

import { Alert, Button, Group, Modal, Stack, Text, Title } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { handleApiError } from 'api/error-handling';
import useCreateWorkoutTemplateFromWorkout from 'hooks/workoutTemplate/useCreateWorkoutTemplateFromWorkout';
import notificationHandler from 'utils/notifications';

interface DeleteWorkoutModalProps {
  opened: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
  workoutId: string;
  workoutName?: string;
}

const DeleteWorkoutModal = ({
  opened,
  onClose,
  onConfirm,
  isLoading = false,
  workoutId,
  workoutName,
}: DeleteWorkoutModalProps): JSX.Element => {
  const { successNotification, errorNotification } = notificationHandler();
  const saveAsTemplateMutation = useCreateWorkoutTemplateFromWorkout();

  const handleSaveAsTemplate = (): void => {
    saveAsTemplateMutation.mutate(workoutId, {
      onSuccess: () => {
        successNotification(
          'Template saved',
          workoutName ? `"${workoutName}" saved as a template.` : 'Workout saved as a template.',
        );
        onConfirm();
      },
      onError: error => {
        errorNotification('Save failed', handleApiError(error).message);
      },
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Title order={4}>Delete workout?</Title>}
      centered
      size='lg'
    >
      <Stack spacing='md'>
        <Text size='md'>
          This workout will be <b>permanently deleted</b>. Athletes won&apos;t see it anymore.
        </Text>

        <Alert icon={<IconAlertTriangle size={16} />} color='yellow' variant='light'>
          <Text size='sm'>Tip: Save it as a template first if you want to reuse it later.</Text>
        </Alert>

        <Group position='right' spacing='xs'>
          <Button variant='subtle' color='gray' onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant='default'
            onClick={handleSaveAsTemplate}
            loading={saveAsTemplateMutation.isLoading}
          >
            Save as Template
          </Button>
          <Button color='red' onClick={onConfirm} loading={isLoading}>
            Delete
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default DeleteWorkoutModal;
